import React, { useEffect, useState } from 'react';
import { Hourglass } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import ConfirmDialog from '../ConfirmDialog';
import { updatePaymentExpiry } from '../../services/organisation.service';
import { clearOrganisationCache } from '../../hooks/useOrganisation';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';

const BASIS_CREATION = 'CREATION';
const BASIS_EXECUTION = 'EXECUTION';

const MAX_EXPIRY_MINUTES = 8760 * 60;
const DEFAULT_EXPIRY_HOURS = 72;

const UNITS = {
    minutes: { api: 'MINUTES', singular: 'minute', plural: 'minutes', minutes: 1 },
    hours: { api: 'HOURS', singular: 'hour', plural: 'hours', minutes: 60 },
    days: { api: 'DAYS', singular: 'day', plural: 'days', minutes: 24 * 60 },
    weeks: { api: 'WEEKS', singular: 'week', plural: 'weeks', minutes: 7 * 24 * 60 },
};

const API_UNIT_TO_KEY = Object.fromEntries(
    Object.entries(UNITS).map(([key, unit]) => [unit.api, key])
);

const CREATION_UNIT_KEYS = ['minutes', 'hours', 'days', 'weeks'];
const EXECUTION_UNIT_KEYS = ['days', 'weeks'];

const BASIS_OPTIONS = [
    { value: BASIS_CREATION, label: 'Creation Date/Time' },
    { value: BASIS_EXECUTION, label: 'Scheduled Date' },
];

const unitKeysForBasis = (basis) => (
    basis === BASIS_EXECUTION ? EXECUTION_UNIT_KEYS : CREATION_UNIT_KEYS
);

const formatDuration = (amount, unit) =>
    `${amount} ${amount === 1 ? UNITS[unit].singular : UNITS[unit].plural}`;

const toAmountAndUnit = (hours) => {
    if (hours % (7 * 24) === 0) return { amount: hours / (7 * 24), unit: 'weeks' };
    if (hours % 24 === 0) return { amount: hours / 24, unit: 'days' };
    return { amount: hours, unit: 'hours' };
};

const durationAsDays = (amount, unit) => {
    const numericAmount = Number(amount);
    const minutesPerUnit = UNITS[unit]?.minutes;
    const maxDays = Math.floor(MAX_EXPIRY_MINUTES / UNITS.days.minutes);
    if (!Number.isInteger(numericAmount) || numericAmount <= 0 || !minutesPerUnit) {
        return { amount: 1, unit: 'days' };
    }
    const days = Math.ceil((numericAmount * minutesPerUnit) / UNITS.days.minutes);
    return { amount: Math.min(Math.max(days, 1), maxDays), unit: 'days' };
};

const readSavedExpiry = ({
    paymentExpiryBasis,
    paymentExpiryValue,
    paymentExpiryUnit,
    paymentExpiryHours,
}) => {
    const fallback = toAmountAndUnit(DEFAULT_EXPIRY_HOURS);
    const unitKey = API_UNIT_TO_KEY[paymentExpiryUnit];

    if (paymentExpiryValue != null && unitKey) {
        const basis = paymentExpiryBasis === BASIS_EXECUTION ? BASIS_EXECUTION : BASIS_CREATION;
        if (!unitKeysForBasis(basis).includes(unitKey)) {
            const converted = durationAsDays(paymentExpiryValue, unitKey);
            return { enabled: true, basis, amount: converted.amount, unit: converted.unit };
        }
        return { enabled: true, basis, amount: paymentExpiryValue, unit: unitKey };
    }

    if (paymentExpiryHours != null) {
        const next = toAmountAndUnit(paymentExpiryHours);
        return { enabled: true, basis: BASIS_CREATION, amount: next.amount, unit: next.unit };
    }

    return { enabled: false, basis: BASIS_CREATION, amount: fallback.amount, unit: fallback.unit };
};

export default function PaymentExpiryCard({
    paymentExpiryBasis,
    paymentExpiryValue,
    paymentExpiryUnit,
    paymentExpiryHours,
    onUpdated,
}) {
    const { user } = useAuth();
    const { toast } = useToast();

    const saved = readSavedExpiry({
        paymentExpiryBasis,
        paymentExpiryValue,
        paymentExpiryUnit,
        paymentExpiryHours,
    });

    const [enabled, setEnabled] = useState(saved.enabled);
    const [basis, setBasis] = useState(saved.basis);
    const [amount, setAmount] = useState(String(saved.amount));
    const [unit, setUnit] = useState(saved.unit);

    useEffect(() => {
        const next = readSavedExpiry({
            paymentExpiryBasis,
            paymentExpiryValue,
            paymentExpiryUnit,
            paymentExpiryHours,
        });
        setEnabled(next.enabled);
        setBasis(next.basis);
        setAmount(String(next.amount));
        setUnit(next.unit);
    }, [paymentExpiryBasis, paymentExpiryValue, paymentExpiryUnit, paymentExpiryHours]);

    const unitKeys = unitKeysForBasis(basis);
    const numericAmount = Number(amount);
    const isWholeAmount = amount !== '' && Number.isInteger(numericAmount) && numericAmount > 0;
    const totalMinutes = isWholeAmount ? numericAmount * UNITS[unit].minutes : null;
    const isInRange = totalMinutes !== null && totalMinutes >= 1 && totalMinutes <= MAX_EXPIRY_MINUTES;
    const maxAmountForUnit = Math.floor(MAX_EXPIRY_MINUTES / UNITS[unit].minutes);
    const unitAllowed = unitKeys.includes(unit);

    let validationError = null;
    if (enabled && !unitAllowed) {
        validationError = 'Choose days or weeks when expiring from the scheduled date.';
    } else if (enabled && !isWholeAmount) {
        validationError = 'Enter a whole number greater than 0.';
    } else if (enabled && !isInRange) {
        validationError = `Maximum is ${formatDuration(maxAmountForUnit, unit)} (1 year).`;
    }

    const isDirty = enabled !== saved.enabled
        || (enabled && (
            basis !== saved.basis
            || unit !== saved.unit
            || numericAmount !== Number(saved.amount)
        ));
    const canSave = isDirty && !validationError;

    const handleBasisChange = (nextBasis) => {
        setBasis(nextBasis);
        if (nextBasis === BASIS_EXECUTION && !EXECUTION_UNIT_KEYS.includes(unit)) {
            const converted = durationAsDays(amount, unit);
            setAmount(String(converted.amount));
            setUnit(converted.unit);
        }
    };

    const handleConfirm = async () => {
        const body = enabled
            ? {
                paymentExpiryBasis: basis,
                paymentExpiryValue: numericAmount,
                paymentExpiryUnit: UNITS[unit].api,
            }
            : {
                paymentExpiryBasis: null,
                paymentExpiryValue: null,
                paymentExpiryUnit: null,
            };
        const response = await updatePaymentExpiry(user.organisationId, body, user.token);

        if (response && response.status === 200) {
            clearOrganisationCache();
            toast({
                title: 'Success',
                description: enabled ? 'Payment expiry updated' : 'Payment expiry disabled',
                variant: 'success',
            });
            onUpdated && onUpdated();
        } else {
            const message = response?.data?.error?.message || 'Error updating payment expiry';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    const basisPhrase = basis === BASIS_EXECUTION ? 'the scheduled date' : 'the creation date';
    const confirmDescription = enabled
        ? `Unauthorised payments will expire ${formatDuration(numericAmount, unit)} after ${basisPhrase}. Save changes?`
        : 'Payments will no longer expire automatically. Save changes?';

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                        <Hourglass className="h-4 w-4 text-slate-500" />
                    </span>
                    <h3 className="my-0">Payment Expiry</h3>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                    Automatically expire payments that have not been fully authorised within a set time.
                </p>

                <label className="flex items-start gap-3 cursor-pointer">
                    <Switch
                        checked={enabled}
                        onCheckedChange={setEnabled}
                        className="mt-0.5"
                    />
                    <span className="text-sm text-gray-600 leading-snug">
                        Expire unauthorised payments
                    </span>
                </label>

                {enabled && (
                    <div className="mt-4 flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm text-gray-600">Expire based on</span>
                            <Select value={basis} onValueChange={handleBasisChange}>
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {BASIS_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {basis === BASIS_EXECUTION && (
                            <p className="text-xs text-gray-500 m-0">
                                If there is no scheduled date present, the creation date will be used.
                            </p>
                        )}

                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 shrink-0">Expire after</span>
                                <Input
                                    type="number"
                                    min="1"
                                    max={maxAmountForUnit}
                                    step="1"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    aria-invalid={Boolean(validationError)}
                                    className="h-9 flex-1 min-w-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <Select value={unit} onValueChange={setUnit}>
                                    <SelectTrigger className="w-[124px] h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {unitKeys.map((key) => (
                                            <SelectItem key={key} value={key}>
                                                {key.charAt(0).toUpperCase() + key.slice(1)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {validationError && (
                                <p className="text-xs text-red-600 mt-2 mb-0">{validationError}</p>
                            )}
                        </div>
                    </div>
                )}

                {canSave ? (
                    <ConfirmDialog
                        title="Update Payment Expiry"
                        description={confirmDescription}
                        cancel="Cancel"
                        action="Update"
                        handleAction={handleConfirm}
                    >
                        <Button className="mt-6 yvr-button">
                            Save Changes
                        </Button>
                    </ConfirmDialog>
                ) : (
                    <Button disabled className="mt-6 yvr-button">
                        Save Changes
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}
